// Event handlers and state management for ModelDataSampleList component

import { useState, useCallback, useEffect, useMemo, MouseEvent, ChangeEvent } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { IMsalContext, useMsal } from '@azure/msal-react';
import { getLocalStorageItem, setLocalStorageItem } from '../../../Services/Global';
import { getPageKeyName } from '../../../Utils/UnitConfigurationHelper';
import { Constants } from '../../../Models/Constants';
import { IUnitConfiguration } from '../../../Models/IUnitConfiguration';
import { ModelDataSampleListApi } from './ModelDataSampleListApi';
import { ModelDataSampleListState, FilterState } from './ModelDataSampleListTypes';
import { getPreferredDisplayUnits, sortData } from './ModelDataSampleListUtils';

/**
 * Custom hook for ModelDataSampleList state management and event handlers
 */
export const useModelDataSampleListHandlers = () => {
    const ctx: IMsalContext = useMsal();
    const navigate = useNavigate();
    const params = useParams();
    const { state } = useLocation() as { state: any };

    const unitInputConfiguration = state.unitInputConfig as IUnitConfiguration;
    const pageKeyName = getPageKeyName(unitInputConfiguration, Constants.ModelDataSampleListPage);
    const filterKeyName = `${unitInputConfiguration.industry}_${unitInputConfiguration.subIndustry}_${unitInputConfiguration.application}_${unitInputConfiguration.subApplication}_${unitInputConfiguration.processStage}_${unitInputConfiguration.productLine}_${unitInputConfiguration.alfaLavalPosition}`;

    // Get stored filter object from localStorage
    const storedFilterObject = useMemo(() => {
        try {
            const stored = getLocalStorageItem(`${filterKeyName}_filterobject`);
            return stored ? JSON.parse(stored) : null;
        } catch (e) {
            console.error('Error parsing stored filter object:', e);
            return null;
        }
    }, [filterKeyName]);

    // Initialize API service
    const apiService = useMemo(() => new ModelDataSampleListApi(ctx, unitInputConfiguration), [ctx, unitInputConfiguration]);

    // Initialize filter state
    const [filterState, setFilterState] = useState<FilterState>({
        fromHotMediaFlowRate: storedFilterObject?.fromHotMediaFlowRate || '',
        toHotMediaFlowRate: storedFilterObject?.toHotMediaFlowRate || '',
        fromHotMediaTempIn: storedFilterObject?.fromHotMediaTempIn || '',
        toHotMediaTempIn: storedFilterObject?.toHotMediaTempIn || '',
        fromHotMediaTempOut: storedFilterObject?.fromHotMediaTempOut || '',
        toHotMediaTempOut: storedFilterObject?.toHotMediaTempOut || '',
        fromHotMediaPressureDrop: storedFilterObject?.fromHotMediaPressureDrop || '',
        toHotMediaPressureDrop: storedFilterObject?.toHotMediaPressureDrop || '',
        
        fromColdMediaFlowRate: storedFilterObject?.fromColdMediaFlowRate || '',
        toColdMediaFlowRate: storedFilterObject?.toColdMediaFlowRate || '',
        fromColdMediaTempIn: storedFilterObject?.fromColdMediaTempIn || '',
        toColdMediaTempIn: storedFilterObject?.toColdMediaTempIn || '',
        fromColdMediaTempOut: storedFilterObject?.fromColdMediaTempOut || '',
        toColdMediaTempOut: storedFilterObject?.toColdMediaTempOut || '',
        fromColdMediaPressureDrop: storedFilterObject?.fromColdMediaPressureDrop || '',
        toColdMediaPressureDrop: storedFilterObject?.toColdMediaPressureDrop || '',
        
        onlyTodo: storedFilterObject?.onlyTodo || false
    });

    // State management
    const [componentState, setComponentState] = useState<ModelDataSampleListState>({
        isLoading: true,
        pageCount: 0,
        currentData: [],
        currentFilter: getLocalStorageItem(`${pageKeyName}_currentFilter`) ?? '',
        currentPage: Number(getLocalStorageItem(pageKeyName)) || 0,
        dataColumns: [],
        configurations: [],
        showFilters: false,
        rowsPerPage: 20,
        filterValue: '',
        filterColumn: '',
        orderBy: '',
        order: 'asc',
        columnMenuAnchor: null,
        activeColumn: '',
        hiddenColumns: [],
        columnManagerOpen: false,
    });

    // Get preferred units
    const preferredUnits = useMemo(() => {
        return getPreferredDisplayUnits(state.unitInputConfig?.flowType);
    }, [state.unitInputConfig?.flowType]);

    /**
     * Create a query string from the filter state
     */
    const createFilterQueryString = useCallback(() => {
        const params = [];

        // Add all numeric filter parameters
        if (!isNaN(parseFloat(filterState.fromHotMediaFlowRate))) {
            params.push(`hotMediaFlowrateFrom=${parseFloat(filterState.fromHotMediaFlowRate)}`);
        }
        if (!isNaN(parseFloat(filterState.toHotMediaFlowRate))) {
            params.push(`hotMediaFlowrateTo=${parseFloat(filterState.toHotMediaFlowRate)}`);
        }
        if (!isNaN(parseFloat(filterState.fromHotMediaTempIn))) {
            params.push(`hotMediaTempInFrom=${parseFloat(filterState.fromHotMediaTempIn)}`);
        }
        if (!isNaN(parseFloat(filterState.toHotMediaTempIn))) {
            params.push(`hotMediaTempInTo=${parseFloat(filterState.toHotMediaTempIn)}`);
        }
        if (!isNaN(parseFloat(filterState.fromHotMediaTempOut))) {
            params.push(`hotMediaTempOutFrom=${parseFloat(filterState.fromHotMediaTempOut)}`);
        }
        if (!isNaN(parseFloat(filterState.toHotMediaTempOut))) {
            params.push(`hotMediaTempOutTo=${parseFloat(filterState.toHotMediaTempOut)}`);
        }
        if (!isNaN(parseFloat(filterState.fromHotMediaPressureDrop))) {
            params.push(`hotMediaPressureDropFrom=${parseFloat(filterState.fromHotMediaPressureDrop)}`);
        }
        if (!isNaN(parseFloat(filterState.toHotMediaPressureDrop))) {
            params.push(`hotMediaPressureDropTo=${parseFloat(filterState.toHotMediaPressureDrop)}`);
        }
        
        if (!isNaN(parseFloat(filterState.fromColdMediaFlowRate))) {
            params.push(`coldMediaFlowrateFrom=${parseFloat(filterState.fromColdMediaFlowRate)}`);
        }
        if (!isNaN(parseFloat(filterState.toColdMediaFlowRate))) {
            params.push(`coldMediaFlowrateTo=${parseFloat(filterState.toColdMediaFlowRate)}`);
        }
        if (!isNaN(parseFloat(filterState.fromColdMediaTempIn))) {
            params.push(`coldMediaTempInFrom=${parseFloat(filterState.fromColdMediaTempIn)}`);
        }
        if (!isNaN(parseFloat(filterState.toColdMediaTempIn))) {
            params.push(`coldMediaTempInTo=${parseFloat(filterState.toColdMediaTempIn)}`);
        }
        if (!isNaN(parseFloat(filterState.fromColdMediaTempOut))) {
            params.push(`coldMediaTempOutFrom=${parseFloat(filterState.fromColdMediaTempOut)}`);
        }
        if (!isNaN(parseFloat(filterState.toColdMediaTempOut))) {
            params.push(`coldMediaTempOutTo=${parseFloat(filterState.toColdMediaTempOut)}`);
        }
        if (!isNaN(parseFloat(filterState.fromColdMediaPressureDrop))) {
            params.push(`coldMediaPressureDropFrom=${parseFloat(filterState.fromColdMediaPressureDrop)}`);
        }
        if (!isNaN(parseFloat(filterState.toColdMediaPressureDrop))) {
            params.push(`coldMediaPressureDropTo=${parseFloat(filterState.toColdMediaPressureDrop)}`);
        }
        
        // Check if onlyTodo is the only filter
        const hasNumericFilters = params.length > 0;
        
        // Add onlyTodo filter parameter as the last parameter
        // This is important for backend URL construction
        if (filterState.onlyTodo) {
            params.push('onlyTodo=true');
        }

        const queryString = params.length > 0 ? `&${params.join('&')}` : '';
        console.log('Generated filter query:', queryString, 'onlyTodo:', filterState.onlyTodo);
        
        return queryString;
    }, [filterState]);

    /**
     * Load design session data with filters
     */
    const loadDesignSessionData = useCallback(async (query?: string, pageNumber?: number, overrideRowsPerPage?: number) => {
        try {
            setComponentState(prev => ({ ...prev, isLoading: true }));
            
            const effectivePageNumber = pageNumber ?? componentState.currentPage;
            const effectivePageSize = overrideRowsPerPage ?? componentState.rowsPerPage;
            
            // Construct the API query
            const filterQuery = query ?? componentState.currentFilter;
            
            const result = await apiService.getDesignSessionData(
                { samplingId: params.samplingId! },
                filterQuery,
                effectivePageNumber,
                effectivePageSize
            );

            if (result.data.length === 0) {
                setComponentState(prev => ({
                    ...prev,
                    currentData: [],
                    dataColumns: [],
                    configurations: [result.config],
                    currentPage: result.currentPageNum,
                    pageCount: result.effectiveRowsPerPage === -1 ? 1 : Math.ceil(result.totalRows / result.pageSize),
                    isLoading: false
                }));
                return;
            }

            // Columns rendered by the table are derived from the keys present in the
            // loaded rows. We:
            //  - aggregate keys across ALL rows (not just the first one) so we don't
            //    drop fields that are missing on a single sample in multi-section mode,
            //  - drop internal/structural fields (`dataSampleStatus`, `sectionId`),
            //  - hide columns whose values are empty for every row in the current
            //    dataset, so different unit types only show relevant columns. A value
            //    is considered empty only when it is null/undefined/''; 0 is treated
            //    as real data so that legitimate zero measurements are not hidden.
            //    Essential columns (`id`, `status`, comment fields) are always kept.
            const EXCLUDED_COLUMN_IDS = new Set(['dataSampleStatus', 'sectionId']);
            const ALWAYS_VISIBLE_COLUMN_IDS = new Set(['id', 'status', 'modelerComment', 'smeComment']);
            const isEmptyCellValue = (value: unknown): boolean =>
                value === undefined || value === null || value === '';

            const aggregatedKeys = new Set<string>();
            result.data.forEach((row: any) => {
                Object.keys(row).forEach(key => aggregatedKeys.add(key));
            });

            const keysWithAnyValue = new Set<string>();
            aggregatedKeys.forEach(key => {
                if (result.data.some((row: any) => !isEmptyCellValue(row[key]))) {
                    keysWithAnyValue.add(key);
                }
            });

            const columns = Array.from(aggregatedKeys)
                .filter(key => !EXCLUDED_COLUMN_IDS.has(key))
                .filter(key => ALWAYS_VISIBLE_COLUMN_IDS.has(key) || keysWithAnyValue.has(key))
                .map(key => ({
                    id: key,
                    label: key.charAt(0).toUpperCase() + key.slice(1),
                    width: { minWidth: 150, maxWidth: 180 },
                    align: 'left' as const,
                    format: (value: any, row: any) => (row as any)[key],
                    sortable: true
                }));

            // Add status column if it doesn't exist
            const statusColumn = {
                id: 'status',
                label: 'Status',
                width: { minWidth: 80, maxWidth: 100 },
                align: 'left' as const,
                format: (value: any, row: any) => row.dataSampleStatus,
                sortable: false
            };

            // Ensure we don't add duplicate status columns
            if (!columns.some(col => col.id === 'status')) {
                columns.unshift(statusColumn);
            }

            setComponentState(prev => ({
                ...prev,
                currentData: result.data,
                dataColumns: columns,
                configurations: [result.config],
                currentPage: result.currentPageNum,
                pageCount: result.effectiveRowsPerPage === -1 ? 1 : Math.ceil(result.totalRows / result.pageSize),
                isLoading: false
            }));
            
            // Store page number in localStorage for persistence
            if (unitInputConfiguration) {
                const keyName = getPageKeyName(unitInputConfiguration, Constants.ModelDataSampleListPage);
                setLocalStorageItem(keyName, result.currentPageNum.toString());
            }

        } catch (error) {
            console.error('Error loading data:', error);
            setComponentState(prev => ({ ...prev, isLoading: false }));
        }
    }, [apiService, params.samplingId, componentState.rowsPerPage, componentState.currentPage, componentState.currentFilter, unitInputConfiguration]);

    /**
     * Handle row click navigation
     */
    const handleRowClick = useCallback((item: any) => {
        navigate(`/modeling/${params.samplingId}/datasamples/${item.id}/review`, { 
            state: { 
                unitConfiguration: unitInputConfiguration, 
                dataSample: item, 
                multiSectionConfiguration: componentState.configurations 
            } 
        });
    }, [navigate, params.samplingId, unitInputConfiguration, componentState.configurations]);

    /**
     * Handle page change event for pagination 
     */
    const handlePageChange = useCallback(async (event: MouseEvent<HTMLButtonElement> | null, page: number) => {
        // Store page number in localStorage
        if (unitInputConfiguration !== undefined) {
            const keyName = getPageKeyName(unitInputConfiguration, Constants.ModelDataSampleListPage);
            setLocalStorageItem(keyName, page.toString());
        }
        
        setComponentState(prev => ({
            ...prev,
            currentPage: page,
            isLoading: true
        }));
        
        // Reload data with new page
        await loadDesignSessionData(componentState.currentFilter, page);
    }, [loadDesignSessionData, componentState.currentFilter, unitInputConfiguration]);

    /**
     * Handle rows per page change
     */
    const handleChangeRowsPerPage = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        const newRowsPerPage = parseInt(event.target.value, 10);
        
        setComponentState(prev => ({
            ...prev,
            rowsPerPage: newRowsPerPage,
            currentPage: 0
        }));
        
        // Reset to first page
        if (unitInputConfiguration !== undefined) {
            const keyName = getPageKeyName(unitInputConfiguration, Constants.ModelDataSampleListPage);
            setLocalStorageItem(keyName, "0");
        }
        
        // Reload data with new page size
        loadDesignSessionData(componentState.currentFilter, 0, newRowsPerPage);
    }, [unitInputConfiguration, componentState.currentFilter, loadDesignSessionData]);

    /**
     * Handle filter application
     */
    const handleApplyFilter = useCallback(() => {
        const filterQuery = createFilterQueryString();
        
        console.log('Applying filter:', filterQuery, 'onlyTodo state:', filterState.onlyTodo);
        
        // Store current filter in localStorage
        setLocalStorageItem(`${pageKeyName}_currentFilter`, filterQuery);
        
        // Store filter object in localStorage
        setLocalStorageItem(`${filterKeyName}_filterobject`, JSON.stringify(filterState));
        
        // Reset to first page
        setLocalStorageItem(pageKeyName, '0');
        
        setComponentState(prev => ({
            ...prev,
            currentFilter: filterQuery,
            currentPage: 0
        }));
        
        // Reload data with new filter
        loadDesignSessionData(filterQuery, 0);
    }, [filterState, createFilterQueryString, filterKeyName, pageKeyName, loadDesignSessionData]);

    /**
     * Handle filter reset
     */
    const handleResetFilter = useCallback(() => {
        // Reset filter state
        setFilterState({
            fromHotMediaFlowRate: '',
            toHotMediaFlowRate: '',
            fromHotMediaTempIn: '',
            toHotMediaTempIn: '',
            fromHotMediaTempOut: '',
            toHotMediaTempOut: '',
            fromHotMediaPressureDrop: '',
            toHotMediaPressureDrop: '',
            
            fromColdMediaFlowRate: '',
            toColdMediaFlowRate: '',
            fromColdMediaTempIn: '',
            toColdMediaTempIn: '',
            fromColdMediaTempOut: '',
            toColdMediaTempOut: '',
            fromColdMediaPressureDrop: '',
            toColdMediaPressureDrop: '',
            
            onlyTodo: false
        });
        
        // Clear filter from localStorage
        setLocalStorageItem(`${pageKeyName}_currentFilter`, '');
        setLocalStorageItem(`${filterKeyName}_filterobject`, JSON.stringify({}));
        
        // Reset to first page
        setLocalStorageItem(pageKeyName, '0');
        
        setComponentState(prev => ({
            ...prev,
            currentFilter: '',
            currentPage: 0
        }));
        
        // Reload data without filter
        loadDesignSessionData('', 0);
    }, [filterKeyName, pageKeyName, loadDesignSessionData]);

    /**
     * Handle filter field change
     */
    const handleFilterChange = useCallback((field: keyof FilterState, value: string | boolean) => {
        setFilterState((prevState: FilterState) => ({
            ...prevState,
            [field]: value
        }));
    }, []);

    /**
     * Handle data filter (keeping legacy method for compatibility)
     */
    const handleDataFilter = useCallback(async (query: string) => {
        if (unitInputConfiguration !== undefined) {
            const keyName = getPageKeyName(unitInputConfiguration, Constants.ModelDataSampleListPage);
            setLocalStorageItem(keyName, '0');
        }
        
        setComponentState(prev => ({
            ...prev,
            currentFilter: query,
            currentPage: 0
        }));
        
        await loadDesignSessionData(query, 0);
    }, [unitInputConfiguration, loadDesignSessionData]);

    /**
     * Handle column-based filtering
     */
    const handleColumnFilterChange = useCallback((columnId: string, value: string) => {
        setComponentState(prev => ({
            ...prev,
            filterColumn: columnId,
            filterValue: value
        }));
        
        // If filter value is empty, clear filter
        if (!value.trim()) {
            setComponentState(prev => ({
                ...prev,
                currentFilter: ''
            }));
            loadDesignSessionData('', componentState.currentPage);
            return;
        }
        
        // Create filter in format supported by backend
        const query = `&filter=${columnId}:${value}`;
        setComponentState(prev => ({
            ...prev,
            currentFilter: query
        }));
        loadDesignSessionData(query, 0);
    }, [componentState.currentPage, loadDesignSessionData]);

    /**
     * Handle sorting request
     */
    const handleRequestSort = useCallback((columnId: string) => {
        const isAsc = componentState.orderBy === columnId && componentState.order === 'asc';
        const newOrder = isAsc ? 'desc' : 'asc';
        
        setComponentState(prev => ({
            ...prev,
            order: newOrder,
            orderBy: columnId
        }));
    }, [componentState.orderBy, componentState.order]);

    /**
     * Column menu handlers
     */
    const handleCloseColumnMenu = useCallback(() => {
        setComponentState(prev => ({
            ...prev,
            columnMenuAnchor: null
        }));
    }, []);

    /**
     * Sort menu handlers
     */
    const handleSortMenuClick = useCallback((direction: 'asc' | 'desc' | null) => {
        if (direction === null) {
            setComponentState(prev => ({
                ...prev,
                orderBy: ''
            }));
        } else {
            setComponentState(prev => ({
                ...prev,
                orderBy: prev.activeColumn,
                order: direction
            }));
        }
        handleCloseColumnMenu();
    }, [handleCloseColumnMenu]);

    const handleOpenColumnMenu = useCallback((e: React.MouseEvent<HTMLElement>, columnId: string) => {
        e.stopPropagation();
        setComponentState(prev => ({
            ...prev,
            columnMenuAnchor: e.currentTarget,
            activeColumn: columnId
        }));
    }, []);

    const handleOpenColumnManager = useCallback(() => {
        setComponentState(prev => ({
            ...prev,
            columnManagerOpen: true
        }));
        handleCloseColumnMenu();
    }, [handleCloseColumnMenu]);

    const handleCloseColumnManager = useCallback(() => {
        setComponentState(prev => ({
            ...prev,
            columnManagerOpen: false
        }));
    }, []);

    const toggleColumnVisibility = useCallback((columnId: string) => {
        setComponentState(prev => {
            const newHiddenColumns = prev.hiddenColumns.includes(columnId)
                ? prev.hiddenColumns.filter(id => id !== columnId)
                : [...prev.hiddenColumns, columnId];
            
            return {
                ...prev,
                hiddenColumns: newHiddenColumns
            };
        });
        handleCloseColumnMenu();
    }, [handleCloseColumnMenu]);

    const isColumnVisible = useCallback((columnId: string) => {
        return !componentState.hiddenColumns.includes(columnId);
    }, [componentState.hiddenColumns]);

    const getVisibleColumns = useCallback(() => {
        return componentState.dataColumns.filter(column => isColumnVisible(column.id));
    }, [componentState.dataColumns, isColumnVisible]);

    /**
     * Ensures the status column is present and positioned first in the columns array
     * If status column doesn't exist, it will be created and added first
     */
    const ensureStatusColumnFirst = useCallback((columns: any[]) => {
        // First, check if status column exists in the visible columns
        const statusColumnIndex = columns.findIndex(col => col.id === 'status');
        
        // If status column exists but is not first, move it to first position
        if (statusColumnIndex > 0) {
            const statusColumn = columns[statusColumnIndex];
            const reorderedColumns = [
                statusColumn,
                ...columns.slice(0, statusColumnIndex),
                ...columns.slice(statusColumnIndex + 1)
            ];
            return reorderedColumns;
        }
        
        // If status column doesn't exist at all, create it and add it first
        if (statusColumnIndex === -1) {
            const statusColumn = {
                id: 'status',
                label: 'Status',
                width: { minWidth: 80, maxWidth: 100 },
                align: 'left' as const,
                sortable: false
            };
            return [statusColumn, ...columns];
        }
        
        // Status column is already first, return columns as is
        return columns;
    }, []);

    /**
     * Toggle filters visibility
     */
    const toggleFilters = useCallback(() => {
        setComponentState(prev => ({
            ...prev,
            showFilters: !prev.showFilters
        }));
    }, []);

    /**
     * Get sorted and filtered data
     */
    const getSortedAndFilteredData = useCallback(() => {
        // For server-side pagination and filtering, we only apply client-side sorting
        // since the data is already paginated and filtered by the server
        return sortData(
            componentState.currentData,
            componentState.orderBy,
            componentState.order
        );
    }, [
        componentState.currentData,
        componentState.orderBy,
        componentState.order
    ]);

    /**
     * Navigate back to dashboard
     */
    const navigateBack = useCallback(() => {
        navigate('/modeling/dashboard');
    }, [navigate]);

    // Load data on component mount
    useEffect(() => {
        loadDesignSessionData();
    }, [loadDesignSessionData]);

    return {
        // State
        componentState,
        preferredUnits,
        filterState,
        
        // Navigation data
        state,
        params,
        
        // Event handlers
        handleRowClick,
        handlePageChange,
        handleChangeRowsPerPage,
        handleDataFilter,
        handleColumnFilterChange,
        handleRequestSort,
        handleSortMenuClick,
        handleOpenColumnMenu,
        handleCloseColumnMenu,
        handleOpenColumnManager,
        handleCloseColumnManager,
        toggleColumnVisibility,
        toggleFilters,
        navigateBack,
        handleFilterChange,
        handleApplyFilter,
        handleResetFilter,
        
        // Data processing
        getSortedAndFilteredData,
        getVisibleColumns,
        isColumnVisible,
        loadDesignSessionData,
        ensureStatusColumnFirst
    };
}; 